import type { CSSProperties } from 'react';
import Modal from 'react-modal';

import ALink from '~/components/features/custom-link';
import ProductGallery from '~/components/partials/product/product-gallery';
import ProductPurchase from '~/components/partials/product/product-purchase';
import { useGeneral } from 'hooks/useGeneral';
import { useItem } from '~/hooks/useItem';
import { useVariantSelection } from '~/hooks/useVariantSelection';
import { awardHref, hasProductVariants } from '~/utils/productGroup';
import { CatalogueItem } from '~/utils/types';
import styles from './quickview-modal.module.scss';

// Overlay full-screen centrado; el contenido (content) se resetea para que la
// tarjeta (.card) maneje todo el look. Neutralizamos los estilos default de
// react-modal.
const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 10000,
  background: 'rgba(16, 16, 30, 0.55)',
  overflowY: 'auto',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '4vh 1rem',
};

const contentStyle: CSSProperties = {
  position: 'relative',
  inset: 'auto',
  border: 'none',
  background: 'transparent',
  padding: 0,
  overflow: 'visible',
  width: '100%',
  maxWidth: '980px',
  margin: 'auto',
};

Modal.setAppElement('#__next');

// Galería + panel con el mismo selector que la ficha. No toca la URL; el
// enlace al detalle lleva a la variante elegida (o al producto sin elegir).
function QuickviewBody({ item, onClose }: { item: CatalogueItem; onClose: () => void }) {
  const selection = useVariantSelection(item, { initialAwardId: null });
  const chosen = selection.variant?.awardId;
  const link = chosen != null ? { href: `/award/${chosen}` } : awardHref(item);

  return (
    <div className={styles.body}>
      <ProductGallery product={item} image={selection.display.image} alt={selection.display.name} />

      <div className={styles.panelWrap}>
        <ProductPurchase product={item} selection={selection} compact />

        <ALink href={link.href} as={link.as} className={styles.detailLink} onClick={onClose}>
          Ver detalle completo
          <i className="fas fa-arrow-right"></i>
        </ALink>
      </div>
    </div>
  );
}

function Quickview() {
  const { modal, closeModal } = useGeneral();
  const { open, item: listItem } = modal;

  // La tarjeta solo trae el resumen del producto: si tiene variantes, la
  // vista rápida pide el detalle (ejes, variantes y sus puntos).
  const needsDetail = Boolean(open && listItem && hasProductVariants(listItem) && !listItem.group);
  const { item: detail, loading } = useItem(listItem?.id, { enabled: needsDetail });
  const item = needsDetail ? detail : listItem;

  if (!open || !listItem) return <div></div>;

  return (
    <Modal
      isOpen={open}
      contentLabel="Vista rápida"
      onRequestClose={closeModal}
      shouldFocusAfterRender={false}
      style={{ overlay: overlayStyle, content: contentStyle }}
      className={styles.content}
      overlayClassName={styles.overlay}
      id="product-quickview"
    >
      <div className={styles.card}>
        <button title="Cerrar (Esc)" type="button" className={styles.close} onClick={closeModal}>
          <i className="fas fa-times"></i>
        </button>

        {item?.award ? (
          <QuickviewBody key={item.id} item={item} onClose={closeModal} />
        ) : (
          <div className={styles.loading} aria-busy={loading}>
            {loading ? (
              <>
                <div className={styles.skelImg}></div>
                <div className={styles.skelLines}>
                  <div className={styles.skelLine}></div>
                  <div className={styles.skelLine}></div>
                  <div className={styles.skelLine}></div>
                </div>
              </>
            ) : (
              <p>No pudimos cargar las opciones de este premio. Ábrelo desde la tienda.</p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default Quickview;
